import React, { useEffect, useState } from "react";
import { createClasses, InputField } from "@bloomberg/media-ui-core";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import styles from "./styles.module.css";
import type {
  PortfolioForm,
  PortfolioFormHolding,
  PortfolioHolding,
} from "~types/portfolio";
import Datepicker from "~components/Datepicker";
import { usePortfolioPage } from "~context/PortfolioPageContext";
import {
  DEFAULT_PORTFOLIO_FORM,
  DEFAULT_PORTFOLIO_LOT,
} from "~constants/Portfolio";
import { usePortfolioFormValidation } from "~context/PortfolioFormValidationContext";

const VALID_NUMBER_REGEX = /^-?\d+(\.\d+)?$/;

type LotLineFields = {
  buyDate: string;
  shares: string;
  buyPrice: string;
};

const isValidNumber = (value: string) => {
  // check if the string value is a number
  return VALID_NUMBER_REGEX.test(value);
};

const getNumericValue = (value: string) => {
  const num = parseFloat(value);

  return isNaN(num) ? 0 : num;
};

function isMobile(userAgent: string) {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    userAgent
  );
}

type LotLineProps = {
  index: number;
  holdingIndex: number;
  lotData: PortfolioHolding;
};

export default function LotLine(props: LotLineProps) {
  const { index, holdingIndex, lotData } = props;

  const { updateValidationState } = usePortfolioFormValidation();
  const { portfolioForm, updatePortfolioForm } = usePortfolioPage();
  const defaultLot = portfolioForm
    ? portfolioForm.holdings[holdingIndex]?.lots[index]
    : DEFAULT_PORTFOLIO_LOT;
  const [lot, setLot] = useState<PortfolioHolding>(defaultLot);

  const [formData, setFormData] = useState<PortfolioForm>(
    portfolioForm || DEFAULT_PORTFOLIO_FORM
  );

  const [validationState, setValidationState] = useState<LotLineFields>({
    buyDate: "",
    shares: "",
    buyPrice: "",
  });

  useEffect(() => {
    setLot(lotData);
  }, [lotData]);

  useEffect(() => {
    if (!portfolioForm) {
      return;
    }

    setFormData(portfolioForm);

    const holding = portfolioForm.holdings[holdingIndex];

    if (holding?.lots[index]) {
      setLot({ ...holding.lots[index] });

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (holding.lots[index].buyDate > Date.now()) {
        // form validation to restrict save
        updateValidationState(`${holding.ticker}-${index}-buyDate`, false);

        // set validation state for UI
        setValidationState({
          ...validationState,
          buyDate: "Future Date not allowed",
        });
      }
    }
  }, [portfolioForm]);

  function updateTotalShares(holding: PortfolioFormHolding) {
    let nextTotalShares = 0;
    let nextCostBasis = 0;

    holding.lots.forEach((holdingLot) => {
      const shares = getNumericValue(holdingLot.shares);

      nextTotalShares += shares;
      nextCostBasis += shares * getNumericValue(holdingLot.buyPrice);
    });

    // Remove trialing zeros
    nextTotalShares = getNumericValue(nextTotalShares.toFixed(4));
    nextCostBasis = getNumericValue(nextCostBasis.toFixed(4));

    return {
      nextTotalShares,
      nextCostBasis,
    };
  }

  const updateLot = (updatedLot: PortfolioHolding) => {
    const holdings: PortfolioFormHolding[] = [...formData.holdings];

    const holding = holdings[holdingIndex];

    holding.lots[index] = updatedLot;

    // Update total shares and cost basis
    const { nextTotalShares, nextCostBasis } = updateTotalShares(holding);

    holding.totalShares = nextTotalShares;
    holding.costBasis = nextCostBasis;

    holdings[holdingIndex] = holding;

    const lotIdentifier = `${holding.ticker}-${index}`;

    // update context validation state for validation on save
    updateValidationState(
      `${lotIdentifier}-shares`,
      isValidNumber(updatedLot.shares)
    );

    updateValidationState(
      `${lotIdentifier}-buyPrice`,
      isValidNumber(updatedLot.buyPrice)
    );

    updateValidationState(
      `${lotIdentifier}-buyDate`,
      Boolean(updatedLot.buyDate)
    );

    updatePortfolioForm({ ...formData, holdings });
  };

  const onSharesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // set validation state for UI
    setValidationState({
      ...validationState,
      shares: event.target.value ? "" : "Shares Is Required",
    });
    updateLot({ ...lot, shares: event.target.value });
  };

  const onPriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // set validation state for UI
    setValidationState({
      ...validationState,
      buyPrice: event.target.value ? "" : "Price Is Required",
    });

    updateLot({ ...lot, buyPrice: event.target.value });
  };

  const onBuyDateChange = (date: Date) => {
    let validationMessage = "";

    if (!date) {
      validationMessage = "Date Is Required";
    } else if (date.getTime() > Date.now()) {
      validationMessage = "Future Date not allowed";
    }

    // set validation state for UI
    setValidationState({
      ...validationState,
      buyDate: validationMessage,
    });
    updateLot({ ...lot, buyDate: date?.getTime() });
  };

  const onHandleRemove = () => {
    trackClickEvent("watchlistDeleteLot", "delete_lot", "click", "watchlist");

    const holdings = [...formData.holdings];

    const holding = holdings[holdingIndex];

    // If this is a last lot then we remove the holding
    if (holding.lots.length === 1) {
      holdings.splice(holdingIndex, 1);
    } else {
      holding.lots.splice(index, 1);

      // Update total shares and cost basis
      const { nextTotalShares, nextCostBasis } = updateTotalShares(holding);

      holding.totalShares = nextTotalShares;
      holding.costBasis = nextCostBasis;

      holdings[holdingIndex] = holding;
    }

    updatePortfolioForm({ ...formData, holdings });
  };

  return (
    <div className={styles.row}>
      <div
        className={createClasses(
          styles.cell,
          styles.noBottomPadding,
          styles.cellFixed
        )}
      >
        <button
          type="button"
          className={styles.removeLotButton}
          onClick={onHandleRemove}
          title="Remove lot"
          aria-label="Remove lot"
          data-testid="remove-lot"
        >
          —
        </button>
      </div>
      <div className={createClasses(styles.cell, styles.noBottomPadding)}>
        <Datepicker
          native={isMobile(window.navigator.userAgent)}
          label=""
          required
          maxDate={new Date()}
          value={lot.buyDate}
          onChange={onBuyDateChange}
          error={validationState.buyDate}
        />
      </div>
      <div className={createClasses(styles.cell, styles.noBottomPadding)}>
        <InputField
          label=""
          size="default"
          type="number"
          value={lot.shares}
          onChange={onSharesChange}
          inputProps={{
            pattern: VALID_NUMBER_REGEX,
          }}
          error={Boolean(validationState.shares)}
        />
      </div>
      <div className={createClasses(styles.cell, styles.noBottomPadding)}>
        <InputField
          label=""
          size="default"
          type="number"
          value={lot.buyPrice}
          onChange={onPriceChange}
          inputProps={{
            pattern: VALID_NUMBER_REGEX,
          }}
          error={Boolean(validationState.buyPrice)}
        />
      </div>
    </div>
  );
}
